<template>
  <div class="overflow-hidden grid grid-rows-[auto_1fr]">
    <div v-if="nodes.length > 0 && hasSearchInput" class="p-1">
      <input
        v-model="searchTerm"
        @input="performSearch"
        :placeholder="$t('common.actions.search')"
        class="w-full px-2 py-1 text-sm border rounded" />
    </div>

    <div class="overflow-y-auto">
      <TreeListNodes
        :nodes="localTreeListNodes"
        :mode="mode"
        uniqueProperty="uid"
        :treeNodeOptions="{
          hasDeleteOption,
          hasAddOption,
          hasEditOption,
          hasRenameOption,
          hasDuplicateOption,
          hasCheckboxes,
        }"
        is-main-tree
        :selected-node-id="selectedNodeId"
        :table="table"
        @move-node="onMoveNode"
        @load-more-nodes="loadMoreNodes"
        :has-checkboxes="hasCheckboxes"
        class="border-t-0" />
      <div
        v-if="localTreeListNodes.length > 0"
        class="flex items-center justify-center w-full items-top min-h-12"
        :class="
          isDraggingOverRoot
            ? 'h-12 bg-theme-50 opacity-60 transition-all ease-out duration-300'
            : ''
        "
        @dragenter.prevent
        @dragover.prevent="isDraggingOverRoot = true"
        @drop.prevent="onDropRoot($event)"
        @dragleave.prevent="isDraggingOverRoot = false">
        <span
          class="pointer-events-none"
          :class="isDraggingOverRoot ? 'text-gray-600' : 'text-white'"
          >{{ $t('tree.dropIntoRoot') }}</span
        >
      </div>

      <TreeListModalAddEditNode
        v-if="isModalAddEditActive"
        :mode="modalAddEditMode"
        :tree-node="modifiedNode"
        @close-modal="onCancelAddEdit"
        @save="handleAddEditModal" />
    </div>
    <ContextMenu
      v-if="mode == 'edit'"
      :options="contextMenuOptions"
      class="fixed"
      :style="{ top: clickLocation.y + 'px', left: clickLocation.x + 'px' }"
      :isDropdownOpen="isContextMenuOpen"
      @action="handleContextMenuAction"
      @close="isContextMenuOpen = false" />
    <InfoModal
      v-if="isConfirmDeleteModalOpen"
      :text="$t('messages.info.deleteNode')"
      :secondaryButtonText="$t('common.actions.delete')"
      :primaryButtonText="$t('common.actions.cancel')"
      @secondaryButtonClicked="onDeleteConfirm"
      @primary-button-clicked="isConfirmDeleteModalOpen = false" />
  </div>
</template>

<script setup lang="ts">
import { provide, ref, watch, onMounted } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';
import TreeListNodes from './TreeListNodes.vue';
import TreeListModalAddEditNode from './TreeListModalAddEditNode.vue';
import ContextMenu from './menus/ContextMenu.vue';
import InfoModal from './InfoModal.vue';

import type {
  ContextMenuOptions,
  HandleTreeNodeOnActionProvider,
  NodeAction,
  TreeType,
} from '@/types/ComponentTypes';
import { useGroupingStore } from '@/stores/grouping';
import { storeToRefs } from 'pinia';
import { deepCopy } from '@/utils/data';

const { selectedGroup, treeNodesLimit } = storeToRefs(useGroupingStore());

const props = withDefaults(
  defineProps<{
    hasDeleteOption?: boolean;
    hasAddOption?: boolean;
    hasEditOption?: boolean;
    hasRenameOption?: boolean;
    hasDuplicateOption?: boolean;
    hasSearchInput?: boolean;
    isMainTree?: boolean;
    inlineEditing?: boolean;
    isAddNewHidden?: boolean;
    mode: 'view' | 'edit';
    selectedNodeId?: string;
    table?: boolean;
    hasCheckboxes?: boolean;
  }>(),
  {
    mode: 'edit',
    table: false,
    hasCheckboxes: false,
  }
);

const nodes = defineModel<TreeType[]>({ default: [] });
const localTreeListNodes = ref<TreeType[]>(deepCopy(nodes.value));
const searchTerm = ref('');

onMounted(() => {
  if (props.hasCheckboxes) {
    updateCountersAndIcons(localTreeListNodes.value);
  }
});

watch(
  () => nodes.value,
  () => {
    performSearch();
    if (props.hasCheckboxes) {
      updateCountersAndIcons(localTreeListNodes.value);
    }
  }
);

function refreshDisplayNodes() {
  localTreeListNodes.value = limitChildren(nodes.value);
}

function limitChildren(nodes: TreeType[]): TreeType[] {
  return nodes.map((node) => {
    const limitedNode: TreeType = { ...node };
    const existingNode = findNode(localTreeListNodes.value, limitedNode.uid);

    limitedNode.visibleChildrenCount = existingNode
      ? existingNode.visibleChildrenCount
        ? existingNode.visibleChildrenCount
        : treeNodesLimit.value
      : treeNodesLimit.value;
    if (limitedNode.children.length > limitedNode.visibleChildrenCount) {
      limitedNode.children = limitedNode.children.slice(0, limitedNode.visibleChildrenCount);
    }

    if (existingNode) limitedNode.expanded = existingNode.expanded;

    limitedNode.children = limitChildren(limitedNode.children);

    return limitedNode;
  });
}

function loadMoreNodes(node: TreeType) {
  const existingNode = findNode(localTreeListNodes.value, node.uid);
  if (existingNode) {
    if (!existingNode.visibleChildrenCount)
      existingNode.visibleChildrenCount = treeNodesLimit.value;
    existingNode.visibleChildrenCount += treeNodesLimit.value;
  }
  refreshDisplayNodes();
}

function performSearch() {
  refreshDisplayNodes();
  if (searchTerm.value.trim() !== '') {
    localTreeListNodes.value = filterNodes(localTreeListNodes.value, searchTerm.value.trim());
  }
}

function filterNodes(nodes: TreeType[], term: string): TreeType[] {
  return nodes
    .map((node) => {
      const matches = node.label.toLowerCase().includes(term.toLowerCase());
      const filteredChildren = filterNodes(node.children, term);

      if (matches || filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren,
        };
      }
      return null;
    })
    .filter((node) => node !== null) as TreeType[];
}

watch(
  () => selectedGroup.value,
  () => {
    searchTerm.value = '';
  }
);

const emit = defineEmits(['deleteNode', 'renameNode', 'clickNode']);

const isModalAddEditActive = ref(false);
const modalAddEditMode = ref<'add' | 'edit'>('add');

const modifiedNode = ref<TreeType>(initNode());

function initNode(): TreeType {
  return {
    uid: uuidv4(),
    label: '',
    children: [],
    expanded: false,
  };
}

function onRenameNode(node: TreeType) {
  modifiedNode.value = node;
  modalAddEditMode.value = 'edit';
  isModalAddEditActive.value = true;
}

function onAddNode(nodeAction: { action: string; node?: TreeType }) {
  let newNodeValue: TreeType = {
    uid: uuidv4(),
    label: '',
    children: [],
    parentId: nodeAction.action == 'add' ? nodeAction.node?.parentId : nodeAction.node?.uid,
    expanded: false,
    action: nodeAction.action,
  };

  if (nodeAction.action === 'add') {
    if (nodeAction.node?.parentId) {
      const parent = findNode(nodes.value, nodeAction.node?.parentId);
      if (parent) {
        newNodeValue.sequence = parent.children.length + 1;
        newNodeValue.rootCode = `${parent.rootCode}.${newNodeValue.sequence}`;
      }
    } else {
      newNodeValue.sequence = nodes.value.length + 1;
      newNodeValue.rootCode = newNodeValue.sequence?.toString();
    }
  }
  if (nodeAction.action === 'addChild' && nodeAction.node) {
    newNodeValue.sequence = nodeAction.node.children.length + 1;
    newNodeValue.rootCode = `${nodeAction.node.rootCode}.${newNodeValue.sequence}`;
  }
  modifiedNode.value = newNodeValue;
  modalAddEditMode.value = 'add';
  isModalAddEditActive.value = true;
}

function handleAddNode(node: TreeType) {
  if (!node.parentId) nodes.value.push(node);
  else {
    if (!activeNode.value) return;
    const parent = findNode(nodes.value, node.parentId);
    if (!parent) return;
    parent.expanded = true;
    parent.children.push(node);
    if (parent.initialChildrenCount) parent.initialChildrenCount++;
    else parent.initialChildrenCount = 1;
  }
  nodes.value = [...nodes.value];
}

function onCancelAddEdit() {
  isModalAddEditActive.value = false;
  modifiedNode.value = initNode();
}

function handleAddEditModal(node: TreeType) {
  if (!node.action) {
    emit('renameNode', node);
  } else {
    handleAddNode(node);
  }
  isModalAddEditActive.value = false;
}

function onExpandNode(node: TreeType) {
  const updateNode = findNode(localTreeListNodes.value, node.uid);
  if (updateNode) updateNode.expanded = !updateNode.expanded;
  localTreeListNodes.value = [...localTreeListNodes.value];
  nodes.value = [...localTreeListNodes.value];
}

function onResizeAll(nodeAction: { node: TreeType; action: string }) {
  const updateNode = findNode(localTreeListNodes.value, nodeAction.node.uid);
  if (!updateNode) return;
  updateNode.expanded = nodeAction.action == 'expand';
  updateNode.children.forEach((child) => onResizeAll({ node: child, action: nodeAction.action }));
  localTreeListNodes.value = [...localTreeListNodes.value];
  isContextMenuOpen.value = false;
}

const { t: $t } = useI18n();
const toastStore = useToastStore();

function onMoveNode(nodeAction: { nodeId: string; targetNode: TreeType }) {
  if (!nodes.value) return;
  const nodeToMove = findNode(nodes.value, nodeAction.nodeId);
  if (!nodeToMove) return;

  // Can't move node in it's own children or it self
  if (isTargetNodeInSubtree(nodeToMove, nodeAction.targetNode.uid)) {
    toastStore.error({ text: $t('messages.errors.invalidNodeMove') });
    return;
  }

  findAndRemoveNode(nodes.value, nodeToMove.uid);

  if (nodeToMove?.parentId) {
    const originalParent = findNode(nodes.value, nodeToMove?.parentId);
    if (originalParent) recalculateChildrenRootCode(originalParent);
  }

  const parent = findNode(nodes.value, nodeAction.targetNode.uid);
  if (parent) {
    nodeToMove.parentId = parent.uid;
    nodeToMove.sequence = parent.children.length + 1;
    nodeToMove.rootCode = `${parent.rootCode}.${nodeToMove.sequence}`;
    if (nodeToMove.children.length) recalculateChildrenRootCode(nodeToMove);
    parent.children.push(nodeToMove);
    if (parent.initialChildrenCount) parent.initialChildrenCount++;
    else parent.initialChildrenCount = 1;
  }
  refreshDisplayNodes();
}

function isTargetNodeInSubtree(root: TreeType, targetId: string): boolean {
  if (root.uid === targetId) return true;
  for (const child of root.children) {
    if (isTargetNodeInSubtree(child, targetId)) return true;
  }
  return false;
}

const isDraggingOverRoot = ref(false);

function onDropRoot(event: DragEvent) {
  isDraggingOverRoot.value = false;
  if (!event.dataTransfer) return;
  const nodeId = event.dataTransfer.getData('nodeId');
  if (!nodeId) return;
  moveNodeToRoot(nodeId);
}

function moveNodeToRoot(nodeToMoveId: string) {
  if (!nodes.value) return;
  const nodeToMove = findNode(nodes.value, nodeToMoveId);
  if (!nodeToMove || !nodeToMove.parentId) return;
  findAndRemoveNode(nodes.value, nodeToMove.uid);
  nodeToMove.parentId = null;
  nodeToMove.sequence = nodes.value.length + 1;
  nodeToMove.rootCode = nodeToMove.sequence.toString();
  if (nodeToMove.children) recalculateChildrenRootCode(nodeToMove);
  nodes.value.push(nodeToMove);
  refreshDisplayNodes();
}

function findNode(tree: TreeType[], nodeId: string): TreeType | null {
  for (const node of tree) {
    if (node.uid == nodeId) return node;
    if (node.children && node.children.length > 0) {
      const foundNode = findNode(node.children, nodeId);
      if (foundNode) return foundNode;
    }
  }
  return null;
}

function findAndRemoveNode(treeNodes: TreeType[], uid: string) {
  const index = treeNodes.findIndex((item) => item.uid == uid);
  if (index !== -1) {
    const parentId = treeNodes[index].parentId;
    if (parentId) {
      const parent = findNode(treeNodes, parentId);
      if (parent && parent.initialChildrenCount && parent.initialChildrenCount > 0)
        parent.initialChildrenCount--;
    }
    treeNodes.splice(index, 1);
    return;
  } else {
    for (const treeNode of treeNodes) {
      if (treeNode.children && treeNode.children.length > 0) {
        findAndRemoveNode(treeNode.children, uid);
      }
    }
  }
}

function recalculateChildrenRootCode(parentNode: TreeType) {
  parentNode.children.forEach((childNode, index) => {
    childNode.sequence = index + 1;
    childNode.rootCode = `${parentNode.rootCode}.${childNode.sequence}`;
    if (childNode.children.length) recalculateChildrenRootCode(childNode);
  });
}

// Context Menu Functions
const isContextMenuOpen = ref(false);
const contextMenuOptions = ref<ContextMenuOptions[]>([]);
const clickLocation = ref({ x: 0, y: 0 });
const contextMenuNode = ref();
function handleContextMenuAction(action: string) {
  if (action == 'delete') {
    onDelete();
  } else {
    handleTreeNodeOnAction({ action, node: contextMenuNode.value });
  }
  isContextMenuOpen.value = false;
}
function onToggleContextMenu(nodeAction: NodeAction) {
  if (nodeAction.contextMenuOptions) contextMenuOptions.value = nodeAction.contextMenuOptions;
  if (nodeAction.clickLocation) clickLocation.value = nodeAction.clickLocation;
  if (nodeAction.node) contextMenuNode.value = nodeAction.node;
  isContextMenuOpen.value = true;
}

const isConfirmDeleteModalOpen = ref(false);
function onDelete() {
  if (contextMenuNode.value && contextMenuNode.value.children.length)
    isConfirmDeleteModalOpen.value = true;
  else handleTreeNodeOnAction({ action: 'delete', node: contextMenuNode.value });

  findAndRemoveNode(nodes.value, contextMenuNode.value.uid);
  const parent = findNode(nodes.value, contextMenuNode.value.parentId);
  if (parent) recalculateChildrenRootCode(parent);
}

function onDeleteConfirm() {
  isConfirmDeleteModalOpen.value = false;
  handleTreeNodeOnAction({ action: 'delete', node: contextMenuNode.value });
}

// HandleOnAction

provide('handleOnAction', handleTreeNodeOnAction as HandleTreeNodeOnActionProvider);

const activeNode = ref<TreeType>();
function handleTreeNodeOnAction(nodeAction: NodeAction) {
  activeNode.value = nodeAction.node;
  switch (nodeAction.action) {
    case 'rename':
      if (!nodeAction.node) break;
      onRenameNode(nodeAction.node);
      break;
    case 'delete':
      if (!nodeAction.node) break;
      emit('deleteNode', nodeAction.node);
      break;
    case 'expand':
      if (!nodeAction.node) break;
      onResizeAll({ action: nodeAction.action, node: nodeAction.node });
      break;
    case 'collapse':
      if (!nodeAction.node) break;
      onResizeAll({ action: nodeAction.action, node: nodeAction.node });
      break;
    case 'add':
      onAddNode(nodeAction);
      break;
    case 'addChild':
      onAddNode(nodeAction);
      break;
    case 'expandNode':
      if (!nodeAction.node) break;
      onExpandNode(nodeAction.node);
      break;
    case 'selectNode':
      if (!nodeAction.node) break;
      onSelectNode(nodeAction.node);
      break;
    case 'clickNode':
      if (!nodeAction.node) break;
      emit('clickNode', nodeAction.node);
      break;
    case 'editNode':
      break;
    case 'toggleContextMenu':
      onToggleContextMenu(nodeAction);
      break;
    default:
      break;
  }
}

function onSelectNode(node: TreeType) {
  if (!props.hasCheckboxes) return;

  const updateNode = findNode(localTreeListNodes.value, node.uid);
  if (!updateNode) return;
  Object.assign(updateNode, node);
  const foundNode = findNode(nodes.value, node.uid);
  if (foundNode) {
    Object.assign(foundNode, node);
  }
  updateSelectInChildNodes(updateNode);
  updateCountersAndIcons(localTreeListNodes.value);
}

function updateSelectInChildNodes(node: TreeType) {
  if (node.children.length > 0) {
    for (const child of node.children) {
      child.selected = node.selected;
      updateSelectInChildNodes(child);
    }
  }
}

function updateCountersAndIcons(tree: TreeType[]) {
  for (const node of tree) {
    if (node.children && node.children.length > 0) {
      const selectedChildren = node.children.filter((x) => x.selected);
      node.counter = selectedChildren.length;
      node.children.length === selectedChildren.length
        ? (node.selectIcon = 'check')
        : (node.selectIcon = 'dash');
    }
    updateCountersAndIcons(node.children);
  }
  localTreeListNodes.value = [...localTreeListNodes.value];
}
</script>
